import React, { useReducer, useMemo, useState, useContext } from "react";
import PropTypes from "prop-types";
import MLBPlayerSummaryContext from "../contexts/MLBPlayerSummaryContext";
import PositionsContext from "../contexts/PositionsContext";
import {
  ALL_ROSTERS,
  AVAILABLE_LIST,
  FINAL_ROSTER,
  INTEREST_LIST,
  PROVISIONAL_LIST,
  ROSTER_LIST_HIERARCHY
} from "../constants/RosterListConstants";
import { QUALIFYING_ID, MAIN_ID } from "../constants/TournamentTypeConstants";

const DEFAULT_POSITIONS_FILTER_OPTION = {
  value: -1,
  label: "All Players",
  name: "All Players",
  orderNum: -1,
  playerType: null
};
const DEFAULT_ROSTER_LIST_OPTION = { label: ALL_ROSTERS, value: ALL_ROSTERS };
const DEFAULT_SORT_BY_OPTION = { label: "Sort Alpha", value: "alpha" };
const DEFAULT_STATUS_SELECTED = { label: "All Club Request Statuses", value: null };
const DEFAULT_TOURNAMENT_TYPE_FILTER = { label: "Qualifiers & Main", value: -1 };

const ROSTER_LIST_OPTIONS = [
  DEFAULT_ROSTER_LIST_OPTION,
  { label: INTEREST_LIST, value: INTEREST_LIST },
  { label: AVAILABLE_LIST, value: AVAILABLE_LIST },
  { label: PROVISIONAL_LIST, value: PROVISIONAL_LIST },
  { label: FINAL_ROSTER, value: FINAL_ROSTER }
];

const TOURNAMENT_TYPE_OPTIONS = [
  { label: "Qualifiers & Main", value: -1 },
  { label: "Main", value: MAIN_ID },
  { label: "Qualifiers", value: QUALIFYING_ID }
];

const SORT_BY_OPTIONS = [DEFAULT_SORT_BY_OPTION, { label: "Sort Numerical", value: "numerical" }];

const INITIAL_STATE = {
  fedFilter: null,
  orgFilter: null,
  playerSummaries: [],
  positionFilter: DEFAULT_POSITIONS_FILTER_OPTION,
  rosterListFilter: DEFAULT_ROSTER_LIST_OPTION,
  tournamentTypeFilter: DEFAULT_TOURNAMENT_TYPE_FILTER,
  sortBy: DEFAULT_SORT_BY_OPTION,
  playerStatuses: [],
  statusSelected: DEFAULT_STATUS_SELECTED,
  nameSearchFilter: null,
  sortFilters: {
    col: "lastName",
    dir: "ASC"
  },
  playersDisplayed: []
};

const MLBPlayerSummaryContextProvider = ({ children }) => {
  const { profilePositionsDropdown } = useContext(PositionsContext).state;

  const [fedFilter, setFedFilter] = useState(INITIAL_STATE.fedFilter);
  const [orgFilter, setOrgFilter] = useState(INITIAL_STATE.orgFilter);
  const [playerSummaries, setPlayerSummaries] = useState(INITIAL_STATE.playerSummaries);
  const [playersDisplayed, setPlayersDisplayed] = useState(INITIAL_STATE.playersDisplayed);
  const [positionFilter, setPositionFilter] = useState([INITIAL_STATE.positionFilter]);
  const [rosterListFilter, setRosterListFilter] = useState(INITIAL_STATE.rosterListFilter);
  const [tournamentTypeFilter, setTournamentTypeFilter] = useState(INITIAL_STATE.tournamentTypeFilter);
  const [sortBy, setSortBy] = useState(INITIAL_STATE.sortBy);
  const [playerStatuses, setPlayerStatuses] = useState(INITIAL_STATE.playerStatuses);
  const [statusSelected, setStatusSelected] = useState(INITIAL_STATE.statusSelected);
  const [sortFilters, setSortFilters] = useState(INITIAL_STATE.sortFilters);

  const isOnRosterList = (highestRosterStatus, rosterList) => {
    const rosterListHierarchy = ROSTER_LIST_HIERARCHY[highestRosterStatus];
    return (rosterListHierarchy && rosterListHierarchy.includes(rosterList)) || rosterList === ALL_ROSTERS;
  };

  const filteredPlayerSummaries = useMemo(() => {
    return (
      playerSummaries
        ?.filter(
          player =>
            !positionFilter ||
            positionFilter.length === 0 ||
            positionFilter?.map(p => p.value).includes(DEFAULT_POSITIONS_FILTER_OPTION.value) ||
            positionFilter?.map(p => p.label).includes(player.position)
        )
        ?.filter(player => player.fedTeamStatusId === tournamentTypeFilter.value || tournamentTypeFilter.value === -1)
        ?.filter(player => isOnRosterList(player.highestRoster, rosterListFilter.value))
        // filter out prospective players from the list - not option of roster list filter
        .filter(player => isOnRosterList(player.highestRoster, INTEREST_LIST))
    );
  }, [playerSummaries, positionFilter, rosterListFilter.value, tournamentTypeFilter.value]);

  const positionFilterOptions = [DEFAULT_POSITIONS_FILTER_OPTION].concat(
    profilePositionsDropdown.filter(p => p.playerType).sort((a, b) => a.orderNum - b.orderNum)
  );

  const tournamentTypeOptions = TOURNAMENT_TYPE_OPTIONS;

  const resetState = () => {
    setPlayerSummaries(INITIAL_STATE.playerSummaries);
    setPositionFilter([INITIAL_STATE.positionFilter]);
    setRosterListFilter(INITIAL_STATE.rosterListFilter);
    setTournamentTypeFilter(INITIAL_STATE.tournamentTypeFilter);
    setSortBy(INITIAL_STATE.sortBy);
    setPlayerStatuses(INITIAL_STATE.playerStatuses);
  };

  const sortPlayerCounts = (playerCounts, alphaField) => {
    return sortBy?.value === "numerical"
      ? playerCounts.sort((a, b) => b.playerCount - a.playerCount)
      : playerCounts.sort((a, b) => a[alphaField].localeCompare(b[alphaField]));
  };

  const reducer = (state, action) => {
    switch (action.type) {
      case "sortPlayers":
        setPlayerSummaries(action.playersDisplayed);
        return {
          ...state,
          sortFilters: {
            ...state.sortFilters,
            col: action.col,
            dir: action.dir
          },
          playersDisplayed: [...action.playersDisplayed]
        };
      default:
        return { ...state };
    }
  };
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  return (
    <MLBPlayerSummaryContext.Provider
      value={{
        state,
        dispatch,
        sortFilters,
        fedFilter,
        filteredPlayerSummaries,
        orgFilter,
        positionFilter,
        positionFilterOptions,
        resetState,
        rosterListFilter,
        rosterListOptions: ROSTER_LIST_OPTIONS,
        setFedFilter,
        setOrgFilter,
        setPlayerSummaries,
        setPositionFilter,
        setRosterListFilter,
        setSortBy,
        sortBy,
        sortByOptions: SORT_BY_OPTIONS,
        sortPlayerCounts,
        playerStatuses,
        playerSummaries,
        setPlayerStatuses,
        statusSelected,
        setStatusSelected,
        tournamentTypeFilter,
        setTournamentTypeFilter,
        tournamentTypeOptions,
        DEFAULT_POSITIONS_FILTER_OPTION
      }}
    >
      {children}
    </MLBPlayerSummaryContext.Provider>
  );
};

MLBPlayerSummaryContextProvider.propTypes = {
  children: PropTypes.object
};

export default MLBPlayerSummaryContextProvider;
